import React from 'react';
import {
  Card,
  CardBody,
  Typography,
  Chip,
} from '@material-tailwind/react';
import {
  CurrencyDollarIcon,
  ClockIcon,
  ServerStackIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';

const formatCost = (cost) => {
  if (cost === null || cost === undefined) return '$0.00';
  const num = typeof cost === 'number' ? cost : parseFloat(cost);
  if (isNaN(num)) return '$0.00';
  return `$${num.toFixed(2)}`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString();
};

const formatTokens = (tokens) => {
  if (!tokens && tokens !== 0) return '0';
  return tokens.toLocaleString();
};

const formatPercent = (rate) => {
  if (rate === null || rate === undefined) return '0%';
  return `${(rate * 100).toFixed(0)}%`;
};

function AICostsPanel({ draft }) {
  const totalCost = draft?.totalCost || 0;
  const costHistory = draft?.costHistory || [];
  const latestEntry = costHistory.length > 0 ? costHistory[costHistory.length - 1] : null;

  return (
    <Card className="h-full flex flex-col">
      <CardBody className="flex-1 flex flex-col p-4 overflow-auto">
        <div className="flex items-center gap-2 mb-4">
          <CurrencyDollarIcon className="h-5 w-5 text-purple-500" />
          <Typography variant="h6" color="blue-gray">
            AI Generation Costs
          </Typography>
        </div>

        {/* Total Cost Summary */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <Typography variant="small" color="gray" className="mb-1">
                Total Cost for This Draft
              </Typography>
              <Typography variant="h4" color="purple" className="font-bold">
                {formatCost(totalCost)}
              </Typography>
            </div>
            {latestEntry && (
              <div className="text-right">
                <Typography variant="small" color="gray" className="mb-1">
                  Last Generation
                </Typography>
                <Typography variant="h6" color="purple">
                  {formatCost(latestEntry.cost)}
                </Typography>
              </div>
            )}
          </div>
          <Typography variant="small" color="gray">
            {costHistory.length} generation{costHistory.length !== 1 ? 's' : ''}
          </Typography>
        </div>

        {/* Latest Generation Stats */}
        {latestEntry && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            {/* Cache Efficiency */}
            {latestEntry.cache && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <ServerStackIcon className="h-4 w-4 text-green-600" />
                  <Typography variant="small" className="font-medium text-green-800">
                    Cache Efficiency
                  </Typography>
                </div>
                <Typography variant="h6" color="green" className="font-bold">
                  {formatPercent(latestEntry.cache.hitRate)} hit rate
                </Typography>
                <Typography variant="small" color="gray" className="text-xs">
                  {latestEntry.cache.hits} cached, {latestEntry.cache.misses} new
                </Typography>
              </div>
            )}

            {/* Prompt Cache Savings */}
            {latestEntry.promptCache && latestEntry.promptCache.savings > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <BoltIcon className="h-4 w-4 text-blue-600" />
                  <Typography variant="small" className="font-medium text-blue-800">
                    Prompt Cache Savings
                  </Typography>
                </div>
                <Typography variant="h6" color="blue" className="font-bold">
                  {formatCost(latestEntry.promptCache.savings)} saved
                </Typography>
                <Typography variant="small" color="gray" className="text-xs">
                  {formatTokens(latestEntry.promptCache.cachedTokens)} cached tokens
                </Typography>
              </div>
            )}
          </div>
        )}

        {/* Cost History */}
        {costHistory.length > 0 ? (
          <div className="space-y-3">
            <Typography variant="small" className="font-semibold text-gray-700">
              Generation History
            </Typography>
            {[...costHistory].reverse().map((entry, index) => (
              <div
                key={index}
                className="border rounded-lg p-3 bg-gray-50"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Chip
                      size="sm"
                      value={`#${costHistory.length - index}`}
                      color="gray"
                      variant="ghost"
                    />
                    <Typography variant="small" className="font-medium">
                      {formatCost(entry.cost)}
                    </Typography>
                  </div>
                  <div className="flex items-center gap-1 text-gray-500">
                    <ClockIcon className="h-3 w-3" />
                    <Typography variant="small" className="text-xs">
                      {formatDate(entry.timestamp)}
                    </Typography>
                  </div>
                </div>

                {/* Token usage */}
                <div className="flex gap-4 mb-2 text-xs text-gray-600">
                  <span>Input: {formatTokens(entry.inputTokens)}</span>
                  {entry.cachedInputTokens > 0 && (
                    <span className="text-green-600">
                      ({formatTokens(entry.cachedInputTokens)} cached)
                    </span>
                  )}
                  <span>Output: {formatTokens(entry.outputTokens)}</span>
                </div>

                {/* Breakdown by source */}
                {entry.breakdown && entry.breakdown.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <Typography variant="small" color="gray" className="text-xs mb-2">
                      Breakdown by Source
                    </Typography>
                    <div className="space-y-1">
                      {entry.breakdown.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs bg-white rounded px-2 py-1">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-700 font-medium capitalize">
                              {item.source.replace(/_/g, ' ')}
                            </span>
                            {item.count > 1 && (
                              <Chip size="sm" value={`×${item.count}`} variant="ghost" color="gray" className="py-0 text-xs" />
                            )}
                          </div>
                          <span className="font-medium">{formatCost(item.cost)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cache stats for this entry */}
                {entry.cache && (
                  <div className="mt-2 pt-2 border-t border-gray-200 flex gap-4 text-xs">
                    <span className="text-green-600">
                      Cache: {formatPercent(entry.cache.hitRate)} ({entry.cache.hits}/{entry.cache.hits + entry.cache.misses})
                    </span>
                    {entry.promptCache?.savings > 0 && (
                      <span className="text-blue-600">
                        Prompt cache saved: {formatCost(entry.promptCache.savings)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <Typography variant="small">No generation history available</Typography>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default AICostsPanel;
